import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getBlogPost,
  addBlogPost,
  updateBlogPost,
  deleteBlogPost,
} from "../../firebase/blogServices";
import { storage } from "../../firebase/config";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import "./Admin.css";
import SpaceParticles from "../../components/Particles/SpaceParticles";
import { FaSave, FaArrowLeft, FaImage, FaTimes, FaTrash } from "react-icons/fa";
import RichTextEditor from "../../components/Admin/RichTextEditor";

const PostEditor = () => {
  const { action, postId } = useParams();
  const navigate = useNavigate();
  const isNewPost = action === "new";
  const isDeletePost = action === "delete";

  const [post, setPost] = useState({
    title: "",
    content: "",
    excerpt: "",
    author: "",
    tags: [],
    imageUrl: "",
  });

  const [loading, setLoading] = useState(!isNewPost);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [tagInput, setTagInput] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [skipImageUpload, setSkipImageUpload] = useState(false);

  useEffect(() => {
    const fetchPost = async () => {
      if (isNewPost) return;

      try {
        console.log("Fetching post with ID:", postId);
        const postData = await getBlogPost(postId);
        if (postData) {
          setPost(postData);
          setImagePreview(postData.imageUrl || null);
        } else {
          setError("Post não encontrado.");
          navigate("/admin/dashboard", { replace: true });
        }
      } catch (err) {
        console.error("Error fetching post:", err);
        setError("Falha ao carregar o post.");
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [isNewPost, postId, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPost((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !post.tags.includes(tagInput.trim())) {
      setPost((prev) => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setPost((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setPost((prev) => ({ ...prev, imageUrl: "" }));
  };

  const handleContentChange = (content) => {
    setPost((prev) => ({ ...prev, content: content }));
  };

  const handleSavePost = async () => {
    if (!post.title.trim() || !post.content.trim()) {
      setError("Título e conteúdo são obrigatórios.");
      return;
    }

    if (!isNewPost && !postId) {
      setError("ID do post não encontrado. Não é possível editar.");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Save post without image first
      const postData = {
        ...post,
        imageUrl: post.imageUrl, // Keep the existing image URL if any
      };

      let savedPostId;
      if (isNewPost) {
        savedPostId = await addBlogPost(postData);
      } else {
        await updateBlogPost(postId, postData);
        savedPostId = postId;
      }

      // Then try to upload the image if there is one
      if (imageFile) {
        try {
          const storageRef = ref(
            storage,
            `blog-images/${savedPostId}/${Date.now()}_${imageFile.name}`
          );
          const snapshot = await uploadBytes(storageRef, imageFile);
          const imageUrl = await getDownloadURL(snapshot.ref);

          // Update the post with the image URL
          await updateBlogPost(savedPostId, { imageUrl });
        } catch (err) {
          console.error("Error uploading image:", err);
          // Post is saved but without the image
          navigate("/admin/dashboard");
          return;
        }
      }

      navigate("/admin/dashboard");
    } catch (err) {
      console.error("Error saving post:", err);
      setError("Falha ao salvar o post. Por favor, tente novamente.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isDeletePost) return;

    setSaving(true);
    try {
      await deleteBlogPost(postId);
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      console.error("Error deleting post:", err);
      setError("Falha ao excluir o post. Por favor, tente novamente.");
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-container">
          <div className="admin-loading">
            <div className="loading-spinner"></div>
            <p>Carregando post...</p>
          </div>
        </div>
        <SpaceParticles />
      </div>
    );
  }

  if (isDeletePost) {
    return (
      <div className="admin-page">
        <div className="admin-container">
          <div className="admin-card">
            <h1 className="admin-title">Confirmar Exclusão</h1>
            <p className="delete-confirmation-text">
              Tem certeza que deseja excluir o post "{post.title}"? Esta ação
              não pode ser desfeita.
            </p>

            {error && <div className="admin-error">{error}</div>}

            <div className="admin-actions">
              <button
                className="admin-button admin-button-danger"
                onClick={handleDelete}
                disabled={saving}
              >
                {saving ? "Excluindo..." : "Sim, Excluir"}
              </button>
              <button
                className="admin-button admin-button-secondary"
                onClick={() => navigate("/admin/dashboard")}
                disabled={saving}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
        <SpaceParticles />
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-post-editor">
        <div className="admin-header">
          <button
            className="admin-back-button"
            onClick={() => navigate("/admin/dashboard")}
          >
            <FaArrowLeft /> Voltar
          </button>
          <h1 className="admin-title">
            {isNewPost ? "Criar Novo Post" : "Editar Post"}
          </h1>
          <button
            className="admin-button admin-button-primary save-button"
            onClick={handleSavePost}
            disabled={saving}
          >
            <FaSave /> {saving ? "Salvando..." : "Salvar"}
          </button>
        </div>

        {error && (
          <div className="admin-error">
            {error}
            {error.includes("CORS") && (
              <button
                type="button"
                className="admin-button admin-button-warning"
                onClick={() => {
                  setSkipImageUpload(true);
                  handleSavePost();
                }}
                style={{ marginLeft: "1rem" }}
              >
                Pular upload de imagem
              </button>
            )}
          </div>
        )}

        <div className="post-editor-form">
          <div className="form-group">
            <label htmlFor="title">Título *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={post.title}
              onChange={handleInputChange}
              required
              className="post-title-input"
              placeholder="Digite o título do post"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="author">Autor</label>
              <input
                type="text"
                id="author"
                name="author"
                value={post.author}
                onChange={handleInputChange}
                placeholder="Nome do autor"
              />
            </div>

            <div className="form-group image-upload-group">
              <label>Imagem de Capa</label>
              <div className="image-upload-container">
                {imagePreview ? (
                  <div className="image-preview-container">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="image-preview"
                    />
                    <button
                      className="remove-image-button"
                      onClick={handleRemoveImage}
                      type="button"
                    >
                      <FaTimes />
                    </button>
                  </div>
                ) : (
                  <label className="image-upload-label">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="image-upload-input"
                    />
                    <FaImage /> Selecionar Imagem
                  </label>
                )}
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="excerpt">Resumo</label>
            <input
              type="text"
              id="excerpt"
              name="excerpt"
              value={post.excerpt}
              onChange={handleInputChange}
              placeholder="Breve resumo do post (opcional)"
              className="post-excerpt-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="content">Conteúdo:</label>
            <RichTextEditor
              value={post.content}
              onChange={handleContentChange}
              placeholder="Escreva o conteúdo do post..."
            />
          </div>

          <div className="form-group tags-group">
            <label>Tags</label>
            <div className="tags-input-container">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) =>
                  e.key === "Enter" && (e.preventDefault(), handleAddTag())
                }
                placeholder="Adicione tags e pressione Enter"
                className="tag-input"
              />
              <button
                type="button"
                onClick={handleAddTag}
                className="add-tag-button"
              >
                Adicionar
              </button>
            </div>

            <div className="tags-container">
              {post.tags.map((tag, index) => (
                <div key={index} className="tag-item">
                  <span>{tag}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="remove-tag-button"
                  >
                    <FaTimes />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {isDeletePost && (
        <div className="admin-post-editor">
          <div className="admin-header">
            <h1 className="admin-title">Excluir Post</h1>
            <div className="admin-actions">
              <button
                onClick={() => navigate("/admin/dashboard")}
                className="admin-button admin-button-secondary"
              >
                <FaArrowLeft /> Voltar
              </button>
            </div>
          </div>

          <div className="post-editor-form delete-confirmation">
            <p className="delete-confirmation-text">
              Tem certeza que deseja excluir o post "{post.title}"? Esta ação
              não pode ser desfeita.
            </p>
            <div className="form-actions">
              <button
                type="button"
                onClick={() => navigate("/admin/dashboard")}
                className="admin-button admin-button-secondary"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="admin-button admin-button-danger"
                disabled={saving}
              >
                {saving ? "Excluindo..." : "Excluir Permanentemente"}
              </button>
            </div>
          </div>
        </div>
      )}
      <SpaceParticles />
    </div>
  );
};

export default PostEditor;
